import {Injectable, inject} from '@angular/core';
import {Observable, map, of, switchMap, tap} from 'rxjs';
import {AccountStorePort} from 'src/app/core/entities/account/domain/ports/account-store.port';
import {ACCOUNT_STORE} from 'src/app/core/entities/account/domain/tokens/account-store.token';
import {CONSUMPTION_TYPE} from 'src/app/core/enums/consumption-type.enum';
import {PAYMENT_METHOD} from 'src/app/shared/enums/payment-method.enum';
import {PAYMENT_TYPE} from '../../../../../shared/enums/payment-type.enum';
import {PAYMENT_STATUS} from '../../../../enums/payment-status.enum';
import {IPaymentDetails} from '../../../../interfaces/payment.interface';
import {formatPaymentOperation} from '../../../../services/payments/utils/payments-processors.utils';
import {Payment} from '../model/payment.model';
import {PAYMENT_FEATURE_FLAGS} from '../tokens/payment-feature-flag.token';
import {PaymentContext} from '../value-objects/payment-context.model';
import {PaymentFactoryService} from './payment-factory.service';
import {EntriesChangedEvent} from "../../../../../shared/application/events/entry/entries-changed.event";
import {EVENT_BUS} from "../../../../../shared/domain/tokens/event-bus.token";
import {WalletWidgetFacade} from "@features/wallet/application/facades/wallet-widget.facade";

@Injectable({providedIn: 'root'})
export class ManualPaymentService {
  private readonly featureFlag = inject(PAYMENT_FEATURE_FLAGS);
  private readonly paymentFactory = inject(PaymentFactoryService);
  private readonly accountStore = inject<AccountStorePort>(ACCOUNT_STORE);
  private readonly eventBus = inject(EVENT_BUS);
  private readonly walletFacade = inject(WalletWidgetFacade);

  processPayment(context: PaymentContext, details: IPaymentDetails): Observable<Payment> {
    if (!context.account) {
      throw new Error('Account required');
    }

    const enrichedDetails = this.addTicketSerie(context, details);
    const payment = this.getOrCreatePayment(context, enrichedDetails);

    return of(payment).pipe(
      tap(payment => this.enrichPaymentMetadata(context, enrichedDetails, payment)),
      tap(payment => this.updateAccount(context, enrichedDetails, payment)),
      tap(payment => payment.updateOperation(formatPaymentOperation(payment))),
      tap(payment => payment.updateStatus(PAYMENT_STATUS.completed)),
      switchMap(payment =>
        this.accountStore.saveSelectedAccount(context.account).pipe(
          map(() => payment)
        )
      )
    );
  }

  private addTicketSerie(context: PaymentContext, details: IPaymentDetails): IPaymentDetails {
    if (!details.isTicketGenerated) {
      return details;
    }

    const ticketSerie = context.device.getSimpleSerie(this.featureFlag.isInvoiceEnabled());

    return {...details, ticketSerie};
  }

  private getOrCreatePayment(context: PaymentContext, details: IPaymentDetails): Payment {
    const failedPayment = context.account.getPaymentByStatus(PAYMENT_STATUS.failed);

    if (failedPayment?.canBeReusedWith({
      organization: { _id: context.organization._id },
      drawer: { id: context.drawer.id, cash_drawer_count_id: '' },
    }, details)) {
      failedPayment.updateStatus(PAYMENT_STATUS.completed);

      return failedPayment;
    }

    if (failedPayment) {
      context.account.deletePayment(failedPayment._id);
    }

    return this.paymentFactory.createPayment(context, details);
  }

  private enrichPaymentMetadata(context: PaymentContext, details: IPaymentDetails, payment: Payment): void {
    const additionalData: Record<string, string> = {};

    const isWallet = details.paymentSummary.paymentMethod === PAYMENT_METHOD.wallet;
    const hasComplement = context.account.entries?.some(entry => entry?.consumption?.type === CONSUMPTION_TYPE.complement);
    const walletId = this.walletFacade.walletId();

    if (isWallet && walletId) {
      additionalData.wallet_id = walletId;
    }

    if (hasComplement && context.ticketEvent?._id) {
      additionalData.ticket_event_id = context.ticketEvent._id;
    }

    payment.updateAdditionalData(additionalData);
  }

  private updateAccount(context: PaymentContext, details: IPaymentDetails, payment: Payment): void {
    const {entriesToPay, paymentSummary, entriesRenderedAfterPayment, isAccountClosed} = details;

    context.account.addPayment(payment);

    if (entriesToPay?.length > 0 && paymentSummary.paymentType === PAYMENT_TYPE.byProduct) {
      context.account.updateEntries(entriesRenderedAfterPayment);
      this.eventBus.publish(new EntriesChangedEvent(entriesRenderedAfterPayment, 'updated'));
    }

    if (isAccountClosed) {
      context.account.updateClosed(isAccountClosed);
      context.account.updateClosedAccountTime(new Date());
    }
  }

}
